import React from 'react';
import { Button, Alert, AlertIcon, AlertDescription, Text } from '@chakra-ui/react';
import { StudentState } from '../../../../store/student/types';
import { countries } from 'platform-data';
import {
  Modal,
  InputWithConfirm,
  TextareaWithConfirm,
  RadioOptionsWithConfirm,
  IQuestion,
  CurrencyInputWithConfirm,
  AutocompleteInputWithConfirm,
} from 'shared-components';

import { EducationSponsorship, FundingSummary } from '../../../../types';
import {
  getPageStatus,
  isLoanTypeValid,
  isLoanTypeUnknown,
  isLoanTypeGeneralBank,
  isRestrictedCountry,
  getCountryBanks,
  getPersonNameString,
  loanTypeLabel,
  fromLoanTypeLabel,
} from './utils';
import { LoanType } from 'api-paths-cas';

interface IProps {
  sponsorKey: string | null;
  id: string;
  fundingSummary: FundingSummary;
  saveButtonText: string;
  config: StudentState['config'];
  onSave: (data: EducationSponsorship) => void;
  onClose: () => void;
}

type IComponentProps = Partial<EducationSponsorship> & IProps;

interface IState {
  loanType: string;
  country: string;
  loanName: string;
  sponsorshipAmount: number;
  personName: string;
  loanDesc: string;
  bankName: string;
  personFirstName: string;
  personSurname: string;
  personDesc: string;
  isOtherBankName: boolean;
  countrySuggestions: string[];
  bankNameSuggestions: string[];
  loanDescError: string;
  personDescError: string;
  sponsorshipAmountError: string;
  loanNameError: string;
  countryError: string;
  bankNameError: string;
  loanProviderError: string;
  personFirstNameError: string;
  personSurnameError: string;
}

class EducationSponsor extends React.Component<IComponentProps, IState> {
  countries: Array<string>;

  constructor(props: IComponentProps) {
    super(props);
    this.countries = [...new Set(countries.map(country => country.countryName))];
    const bankName = props.bankName || '';
    const country = props.country || '';
    const isOtherBankName =
      isRestrictedCountry(country) && bankName !== ''
        ? !getCountryBanks(country).some(bank => bank === bankName)
        : false;
    this.state = {
      loanType: props.loanType || '',
      country: props.country || '',
      loanName: props.loanName || '',
      sponsorshipAmount: props.sponsorshipAmount || 0,
      personName: props.personName || '',
      loanDesc: props.loanDesc || '',
      bankName: props.bankName || '',
      personFirstName: props.personFirstName || '',
      personSurname: props.personFirstName || '',
      personDesc: props.personDesc || '',
      isOtherBankName,
      countrySuggestions: [],
      bankNameSuggestions: [],
      loanDescError: '',
      personDescError: '',
      sponsorshipAmountError: '',
      loanNameError: '',
      countryError: '',
      bankNameError: '',
      loanProviderError: '',
      personFirstNameError: '',
      personSurnameError: '',
    };
  }

  componentDidUpdate(_prevProps: IComponentProps, prevState: IState) {
    if (prevState.loanType !== this.state.loanType) {
      if (!isLoanTypeUnknown(this.state.loanType)) {
        this.setState({
          loanDesc: '',
        });
      }
      if (!isLoanTypeValid(this.state.loanType)) {
        this.setState({
          country: '',
          bankName: '',
        });
      }
    }

    if (prevState.personName !== this.state.personName) {
      this.setState({
        personFirstName: '',
        personSurname: '',
        personDesc: '',
      });
    }

    if (prevState.country !== this.state.country || prevState.isOtherBankName !== this.state.isOtherBankName) {
      this.setState({
        bankName: '',
      });
    }
  }

  onBlurLoanDescriptionOther = (_event: React.FormEvent<HTMLInputElement>) => {
    if (this.state.loanDesc === '') {
      this.setState({
        loanDescError: 'Please fill this in',
      });
    }
  };

  onFocusLoanDescriptionOther = (_event: React.FormEvent<HTMLInputElement>) => {
    this.setState({
      loanDescError: '',
    });
  };

  onBlurSponsorshipAmount = (_event: React.FormEvent<HTMLInputElement>) => {
    if (this.state.sponsorshipAmount === 0) {
      this.setState({
        sponsorshipAmountError: 'Please enter a value',
      });
    }
  };

  onFocusSponsorshipAmount = (_event: React.FormEvent<HTMLInputElement>) => {
    this.setState({
      sponsorshipAmountError: '',
    });
  };

  onBlurLoanName = (_event: React.FormEvent<HTMLInputElement>) => {
    if (this.state.loanName === '') {
      this.setState({
        loanNameError: 'Please fill this in',
      });
    }
  };

  onFocusLoanName = (_event: React.FormEvent<HTMLInputElement>) => {
    this.setState({
      loanNameError: '',
    });
  };

  onBlurPersonDesc = (_event: React.FormEvent<HTMLInputElement>) => {
    if (this.state.personDesc === '') {
      this.setState({
        personDescError: 'Please fill this in',
      });
    }
  };

  onFocusPersonDesc = (_event: React.FormEvent<HTMLInputElement>) => {
    this.setState({
      personDescError: '',
    });
  };

  onLoanTypeClick = (question: IQuestion) => {
    this.setState({
      loanType: fromLoanTypeLabel(question.label),
    });
  };

  onLoanDescriptionOtherChange = (event: React.FormEvent<HTMLSelectElement>) => {
    this.setState({
      loanDesc: event.currentTarget.value,
    });
  };

  onLoanNameChange = (event: React.FormEvent<HTMLInputElement>) => {
    this.setState({
      loanName: event.currentTarget.value,
    });
  };

  onLoanProviderChange = (event: React.FormEvent<HTMLInputElement>) => {
    this.setState({
      bankName: event.currentTarget.value,
    });
  };

  onBlurLoanProvider = (_event: React.FormEvent<HTMLInputElement>) => {
    if (this.state.bankName === '') {
      this.setState({
        loanProviderError: 'Please fill this in',
      });
    }
  };

  onFocusLoanProvider = (_event: React.FormEvent<HTMLInputElement>) => {
    this.setState({
      loanProviderError: '',
    });
  };

  onSponsorshipAmountChange = (event: React.FormEvent<HTMLInputElement>) => {
    const sponsorshipAmount = Number(event.currentTarget.value) || 0;
    this.setState({
      sponsorshipAmount,
    });
  };

  onPersonNameClick = (question: IQuestion) => {
    let value = '';

    switch (question.label) {
      case 'My name':
        value = 'Mine';
        break;
      case "My mother's name":
        value = 'Mother';
        break;
      case "My father's name":
        value = 'Father';
        break;
      case "Guardian's name":
        value = 'Guardian';
        break;
      case 'Someone else':
        value = 'Other';
        break;
      default:
        value = '';
        break;
    }

    this.setState({
      personName: value,
    });
  };

  onPersonDescChange = (event: React.FormEvent<HTMLInputElement>) => {
    this.setState({
      personDesc: event.currentTarget.value,
    });
  };

  getPageStatus = () => {
    return getPageStatus({
      loanType: this.state.loanType,
      loanDesc: this.state.loanDesc,
      country: this.state.country,
      bankName: this.state.bankName,
      loanName: this.state.loanName,
      sponsorshipAmount: this.state.sponsorshipAmount,
      personName: this.state.personName,
      personFirstName: this.state.personFirstName,
      personSurname: this.state.personSurname,
      personDesc: this.state.personDesc,
      isOtherBankName: this.state.isOtherBankName,
    });
  };

  onSaveRegionDetails = () => {
    this.props.onSave({
      loanType: this.state.loanType as LoanType,
      country: this.state.country,
      bankName: this.state.bankName,
      loanName: this.state.loanName,
      sponsorshipAmount: this.state.sponsorshipAmount,
      personName: this.state.personName as EducationSponsorship['personName'],
      loanDesc: this.state.loanDesc,
      personFirstName: this.state.personFirstName,
      personSurname: this.state.personSurname,
      personDesc: this.state.personDesc,
    });

    this.props.onClose();
  };

  onBlurCountry = (_event: React.FormEvent<HTMLInputElement>) => {
    const value = this.state.country.trim();
    const countryError = this.countries.includes(value) ? '' : 'Please enter a country';
    const country = this.countries.includes(value) ? value.trim() : '';

    this.setState({
      country,
      countrySuggestions: [],
      countryError,
    });
  };

  onFocusCountry = (_event: React.FormEvent<HTMLInputElement>) => {
    this.setState({
      countryError: '',
    });
  };

  onCountryChange = (evt: any) => {
    const value = evt.currentTarget.value || '';
    const suggestions = this.countries.filter(country => country.toLowerCase().includes(value.toLowerCase()));
    this.setState({
      countrySuggestions: suggestions,
      country: value,
    });
  };

  onBankNameChange = (event: React.FormEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value || '';
    const suggestions = getCountryBanks(this.state.country).filter(bank =>
      bank.toLowerCase().includes(value.toLowerCase()),
    );

    this.setState({
      bankNameSuggestions: suggestions,
      bankName: value,
    });
  };

  onToggleManualBankNameEntry = () => {
    this.setState({
      isOtherBankName: !this.state.isOtherBankName,
    });
  };

  onManualBankNameChange = (event: React.FormEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value || '';
    this.setState({
      bankName: value,
    });
  };

  onBlurManualEntryBankName = (_event: React.FormEvent<HTMLInputElement>) => {
    const value = this.state.bankName.trim();
    const bankNameError = value === '' ? 'Please enter a bank name' : '';
    this.setState({
      bankName: value,
      bankNameError,
    });
  };

  onBlurBankName = (_event: React.FormEvent<HTMLInputElement>) => {
    const value = this.state.bankName.trim();
    const bankName = getCountryBanks(this.state.country).find(bank => bank === value) || '';
    const bankNameError = bankName === '' ? 'Please enter a bank name' : '';

    this.setState({
      bankName,
      bankNameError,
      bankNameSuggestions: [],
    });
  };

  onFocusBankName = (_event: React.FormEvent<HTMLInputElement>) => {
    this.setState({
      bankNameError: '',
    });
  };

  render() {
    const { complete, visible } = this.getPageStatus();

    return (
      <Modal
        id={this.props.id}
        title="Provide details for your loan"
        show={true}
        onClose={this.props.onClose}
        footer={
          <>
            {isLoanTypeGeneralBank(this.state.loanType) ? (
              <Button id="closeButton" onClick={this.props.onClose} size={'lg'}>
                Go back
              </Button>
            ) : (
              <Button
                id="saveButton"
                onClick={this.onSaveRegionDetails}
                isDisabled={!complete}
                size={'lg'}
                variant={'solid'}
              >
                {this.props.saveButtonText}
              </Button>
            )}
          </>
        }
      >
        {visible.loanType && (
          <RadioOptionsWithConfirm
            id={'org_type'}
            label="Which of the below best describes your loan?"
            questions={[
              {
                char: '1',
                label: loanTypeLabel(LoanType.NATIONAL_GOV),
                selected: this.state.loanType === LoanType.NATIONAL_GOV,
              },
              {
                char: '2',
                label: loanTypeLabel(LoanType.STATE_GOV),
                selected: this.state.loanType === LoanType.STATE_GOV,
              },
              {
                char: '3',
                label: loanTypeLabel(LoanType.LOAN_COMP),
                selected: this.state.loanType === LoanType.LOAN_COMP,
              },
              {
                char: '4',
                label: loanTypeLabel(LoanType.LOAN_SCHEME),
                selected: this.state.loanType === LoanType.LOAN_SCHEME,
              },
              {
                char: '5',
                label: 'A general bank loan (not specified as a student loan)',
                selected: this.state.loanType === 'A general bank loan (not specified as a student loan)',
              },
              {
                char: '6',
                label: loanTypeLabel(LoanType.UNKNOWN),
                selected: this.state.loanType === LoanType.UNKNOWN,
              },
              {
                char: '7',
                label: loanTypeLabel(LoanType.OTHER),
                selected: this.state.loanType === LoanType.OTHER,
              },
            ]}
            onClick={this.onLoanTypeClick}
          />
        )}

        {visible.loanDesc && (
          <TextareaWithConfirm
            id="loan-description-other"
            value={this.state.loanDesc}
            label="Please describe your loan below"
            errorText={this.state.loanDescError}
            onChange={this.onLoanDescriptionOtherChange}
            onBlur={this.onBlurLoanDescriptionOther}
            onFocus={this.onFocusLoanDescriptionOther}
          />
        )}

        {visible.country && (
          <AutocompleteInputWithConfirm
            id="country"
            label="Country loan provider is located"
            onChange={this.onCountryChange}
            value={this.state.country}
            showErrorText={!!this.state.countryError}
            errorText={this.state.countryError}
            suggestions={this.state.countrySuggestions}
            onBlur={this.onBlurCountry}
            onFocus={this.onFocusCountry}
          />
        )}

        {visible.loanProvider && (
          <InputWithConfirm
            id="loan-provider"
            value={this.state.bankName}
            label="Loan provider name"
            errorText={this.state.loanProviderError}
            onChange={this.onLoanProviderChange}
            onBlur={this.onBlurLoanProvider}
            onFocus={this.onFocusLoanProvider}
          />
        )}

        {visible.bankName && (
          <>
            <AutocompleteInputWithConfirm
              id="restrictedBanks"
              key="restrictedBanks"
              label="Please select the bank"
              onChange={this.onBankNameChange}
              value={this.state.bankName}
              showErrorText={!!this.state.bankNameError}
              errorText={this.state.bankNameError}
              suggestions={this.state.bankNameSuggestions}
              onBlur={this.onBlurBankName}
              onFocus={this.onFocusBankName}
              mb={3}
            />

            <Button onClick={this.onToggleManualBankNameEntry} mb={10}>
              Can't find your bank on the list?
            </Button>
          </>
        )}

        {visible.bankOtherName && (
          <>
            <InputWithConfirm
              id="restrictedBanks"
              key="restrictedBanks"
              label="Enter bank name not on list"
              value={this.state.bankName}
              errorText={this.state.bankNameError}
              onChange={this.onManualBankNameChange}
              onBlur={this.onBlurManualEntryBankName}
              onFocus={this.onFocusBankName}
              mb={3}
            />

            <Button onClick={this.onToggleManualBankNameEntry} mb={10}>
              Back to list
            </Button>
          </>
        )}

        {visible.loanName && (
          <InputWithConfirm
            id="loan-name"
            value={this.state.loanName}
            label="What is the name of the loan?"
            errorText={this.state.loanNameError}
            onChange={this.onLoanNameChange}
            onBlur={this.onBlurLoanName}
            onFocus={this.onFocusLoanName}
          />
        )}

        {visible.sponsorshipAmount && (
          <CurrencyInputWithConfirm
            id="education-sponsor-amount"
            value={this.state.sponsorshipAmount}
            errorText={this.state.sponsorshipAmountError}
            onChange={this.onSponsorshipAmountChange}
            onFocus={this.onFocusSponsorshipAmount}
            onBlur={this.onBlurSponsorshipAmount}
            label="What is the amount being covered in pounds?"
          />
        )}

        {visible.personName && (
          <RadioOptionsWithConfirm
            id="education-sponsor-type"
            label={`Whose name is on the loan?`}
            questions={[
              {
                char: '1',
                label: 'My name',
                selected: this.state.personName === 'Mine',
              },
              {
                char: '2',
                label: "My father's name",
                selected: this.state.personName === 'Father',
              },
              {
                char: '3',
                label: "My mother's name",
                selected: this.state.personName === 'Mother',
              },
              {
                char: '4',
                label: "Guardian's name",
                selected: this.state.personName === 'Guardian',
              },
              {
                char: '5',
                label: 'Someone else',
                selected: this.state.personName === 'Other',
              },
            ]}
            inputHelperText={this.props.config.Checklist.Finances.Page1.EducationLoanName?.helpText}
            onClick={this.onPersonNameClick}
          />
        )}

        {visible.personFirstName && (
          <InputWithConfirm
            id="personFirstName"
            value={this.state.personFirstName}
            label={`What is your ${getPersonNameString(this.state.personName)}'s first name?`}
            errorText={this.state.personFirstNameError}
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              this.setState({
                personFirstName: event.currentTarget.value,
              });
            }}
            onBlur={(_event: React.FormEvent<HTMLInputElement>) => {
              if (this.state.personFirstName === '') {
                this.setState({
                  personFirstNameError: 'Please fill this in',
                });
              }
            }}
            onFocus={(_event: React.FormEvent<HTMLInputElement>) => {
              this.setState({
                personFirstNameError: '',
              });
            }}
          />
        )}

        {visible.personSurname && (
          <InputWithConfirm
            id="personSurname"
            value={this.state.personSurname}
            label={`What is your ${getPersonNameString(this.state.personName)}'s family name?`}
            errorText={this.state.personSurnameError}
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              this.setState({
                personSurname: event.currentTarget.value,
              });
            }}
            onBlur={(_event: React.FormEvent<HTMLInputElement>) => {
              if (this.state.personSurname === '') {
                this.setState({
                  personSurnameError: 'Please fill this in',
                });
              }
            }}
            onFocus={(_event: React.FormEvent<HTMLInputElement>) => {
              this.setState({
                personSurnameError: '',
              });
            }}
          />
        )}

        {visible.personDesc && (
          <InputWithConfirm
            id="loan-name-other"
            value={this.state.personDesc}
            label="What is your relationship to the person?"
            inputHelperText={this.props.config.Checklist.Finances.Page1.RelationshipToPerson?.helpText}
            errorText={this.state.personDescError}
            onChange={this.onPersonDescChange}
            onBlur={this.onBlurPersonDesc}
            onFocus={this.onFocusPersonDesc}
          />
        )}

        {isLoanTypeGeneralBank(this.state.loanType) ? (
          <Alert status="info">
            <AlertIcon />

            <AlertDescription>
              <Text>
                General bank loans (not specified as a student loan) can be entered as personal funds in the Myself or
                My Family section on the previous page.
              </Text>

              <Text mb={0}>
                If you are unsure of the type of loan you have, select "{loanTypeLabel(LoanType.UNKNOWN)}" above.
              </Text>
            </AlertDescription>
          </Alert>
        ) : null}
      </Modal>
    );
  }
}

export default EducationSponsor;
